import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Share2, Heart, Users, Gift, User, Calendar, Edit, Download, DollarSign } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getRegistry } from '../lib/registry';
import { supabase } from '../lib/supabase';
import { exportRegistryToExcel } from '../utils/excelExport';
import { ThemeProvider } from '../components/ThemeProvider';
import ThemedLayout from '../components/ThemedLayout';
import ShareRegistryModal from '../components/ShareRegistryModal';
import GuestClaimModal from '../components/GuestClaimModal';
import GroupGiftModal from '../components/GroupGiftModal';
import SuccessMessage from '../components/SuccessMessage';
import ThankYouModal from '../components/ThankYouModal';
import EditRegistryModal from '../components/EditRegistryModal';
import EditEventsModal from '../components/EditEventsModal';
import EditItemModal from '../components/EditItemModal';
import ThemeCustomizer from '../components/ThemeCustomizer';
import PaymentModal from '../components/PaymentModal';
import RSVPTable from '../components/RSVPTable';
import MoneyRequestSection from '../components/MoneyRequestSection';
import TransactionBreakdown from '../components/TransactionBreakdown';
import type { Registry, RegistryItem, RegistryEvent, RSVPResponse } from '../types/registry';
import type { ThemeConfig } from '../types/theme';

interface ClaimInfo {
  name: string;
  email: string;
  type: 'user' | 'guest';
}

interface Contribution {
  contributor_name: string;
  amount: number;
  message?: string;
}

export default function RegistryDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [registry, setRegistry] = useState<Registry & { items: RegistryItem[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showGroupGiftModal, setShowGroupGiftModal] = useState(false);
  const [showThankYouModal, setShowThankYouModal] = useState(false);
  const [showEditRegistryModal, setShowEditRegistryModal] = useState(false);
  const [showEditEventsModal, setShowEditEventsModal] = useState(false);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<RegistryItem | null>(null);
  const [events, setEvents] = useState<RegistryEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<string>('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessageType, setSuccessMessageType] = useState<'claim' | 'contribution'>('claim');
  const [claimInfo, setClaimInfo] = useState<Record<string, ClaimInfo>>({});
  const [contributions, setContributions] = useState<Record<string, Contribution[]>>({});
  const [rsvpData, setRsvpData] = useState<RSVPResponse | null>(null);
  const [theme, setTheme] = useState<ThemeConfig>({
    backgroundImage: '',
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    opacity: 0.75,
    fontFamily: 'IntroRust'
  });
  const [showFeeInfo, setShowFeeInfo] = useState(false);

  const isOwner = user?.id === registry?.user_id;

  useEffect(() => {
    console.log('Registry ID from params:', id);
    if (id) {
      loadRegistry();
      loadRSVPData();
    } else {
      console.error('No registry ID provided');
      setError('No registry ID provided');
      setLoading(false);
    }
    const cleanup = subscribeToChanges();
    return () => cleanup();
  }, [id]);

  useEffect(() => {
    if (registry) {
      loadEvents();
      if (isOwner) {
        loadClaimInfo();
        loadContributions();
      }
      if (registry.theme) {
        setTheme(registry.theme);
      }
    }
  }, [registry, isOwner]);

  const loadRSVPData = async () => {
    if (!id) return;
    
    try {
      const { data } = await supabase
        .from('registry_rsvps')
        .select('*')
        .eq('registry_id', id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setRsvpData(data);
      }
    } catch (err) {
      console.error('Error loading RSVP data:', err);
    }
  };


  const loadClaimInfo = async () => {
    if (!registry) return;

    const claimedItems = registry.items.filter(item => item.is_claimed);
    const info: Record<string, ClaimInfo> = {};

    for (const item of claimedItems) {
      if (item.claimed_by) {
        const { data: userData } = await supabase
          .from('profiles')
          .select('username, email')
          .eq('id', item.claimed_by)
          .single();

        if (userData) {
          info[item.id] = {
            name: userData.username,
            email: userData.email || '',
            type: 'user'
          };
        }
      } else if (item.guest_claimed_by) {
        const { data: guestData } = await supabase
          .from('guest_users')
          .select('name, email')
          .eq('id', item.guest_claimed_by)
          .single();

        if (guestData) {
          info[item.id] = {
            name: guestData.name,
            email: guestData.email,
            type: 'guest'
          };
        }
      }
    }

    setClaimInfo(info);
  };

  const loadContributions = async () => {
    if (!registry) return;

    const groupGiftItems = registry.items.filter(item => item.allows_group_gifting);
    const contribs: Record<string, Contribution[]> = {};

    for (const item of groupGiftItems) {
      const { data } = await supabase
        .from('group_gift_contributions')
        .select('contributor_name, amount, message')
        .eq('item_id', item.id)
        .order('created_at', { ascending: false });

      if (data) {
        contribs[item.id] = data;
      }
    }

    setContributions(contribs);
  };

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        .from('registry_events')
        .select('*')
        .eq('registry_id', id)
        .order('date', { ascending: true });

      if (error) throw error;
      if (data) {
        setEvents(data);
        if (data.length > 0 && user) {
          setSelectedEvent(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  const loadRegistry = async () => {
    try {
      setLoading(true);
      setError('');
      console.log('Loading registry with ID:', id);
      
      if (!id) {
        throw new Error('Registry ID is required');
      }

      const data = await getRegistry(id);
      console.log('Registry data loaded:', data);
      setRegistry(data);
    } catch (err) {
      console.error('Error loading registry:', err);
      setError(err instanceof Error ? err.message : 'Failed to load registry');
    } finally {
      setLoading(false);
    }
  };

  const subscribeToChanges = () => {
    if (!id) return () => {};

    const channel = supabase
      .channel(`registry-${id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'registry_items',
        filter: `registry_id=eq.${id}`
      }, () => {
        loadRegistry();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleClaim = (item: RegistryItem) => {
    setSelectedItem(item);
    if (user) {
      setShowPaymentModal(true);
    } else {
      setShowClaimModal(true);
    }
  };

  const handleGroupGift = (item: RegistryItem) => {
    setSelectedItem(item);
    setShowGroupGiftModal(true);
  };

  const handleThankYou = (item: RegistryItem) => {
    setSelectedItem(item);
    setShowThankYouModal(true);
  };

  const handleClaimSuccess = () => {
    setShowClaimModal(false);
    setShowPaymentModal(false);
    setSuccessMessageType('claim');
    setShowSuccessMessage(true);
  };

  const handleContributionSuccess = () => {
    setShowGroupGiftModal(false);
    setSuccessMessageType('contribution');
    setShowSuccessMessage(true);
  };

  const handleSuccessClose = () => {
    setShowSuccessMessage(false);
    loadRegistry();
  };

  const handleEditRegistryClose = useCallback(() => {
    setShowEditRegistryModal(false);
  }, []);

  const handleExport = async () => {
    if (!registry) return;
    await exportRegistryToExcel(registry);
  };

  const handleSkipGift = () => {
    navigate(-1);
  };

  const getFeeResponsibilityLabel = (responsibility: string) => {
    switch (responsibility) {
      case 'GUEST': return 'Guest Pays Fee';
      case 'COUPLE': return 'Couple Absorbs Fee';
      case 'SPLIT': return 'Fee Split 50/50';
      default: return 'Guest Pays Fee';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-red-400 text-lg mb-4">{error}</p>
          <button
            onClick={() => navigate('/registries')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Back to Registries
          </button>
        </div>
      </div>
    );
  }

  if (!registry) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center py-12">
          <p className="text-gray-400 text-lg mb-4">Registry not found</p>
          <button
            onClick={() => navigate('/registries')}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            Back to Registries
          </button>
        </div>
      </div>
    );
  }

  return (
    <ThemeProvider initialTheme={theme}>
      <ThemedLayout>
        <div className="max-w-4xl mx-auto space-y-8 px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="w-full sm:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
                <h1 className="text-2xl sm:text-3xl font-bold break-words">{registry.title}</h1>
                {!user && (
                  <div className="flex items-center mt-2 sm:mt-0 text-lg">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span>{new Date(registry.event_date).toLocaleDateString(undefined, {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}</span>
                  </div>
                )}
              </div>
              {registry.description && (
                <p className="mt-2 text-sm sm:text-base">{registry.description}</p>
              )}
              {events.length > 0 && user && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">
                    Select Event
                  </label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full sm:w-auto px-3 py-2 bg-gray-900/50 border border-gray-700 rounded-md focus:ring-primary focus:border-primary text-sm"
                  >
                    {events.map(event => (
                      <option key={event.id} value={event.id}>
                        {event.name} - {new Date(event.date).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mt-2 space-y-1 text-sm opacity-75">
                {user && <p>Event Date: {new Date(registry.event_date).toLocaleDateString()}</p>}
                {registry.delivery_address && (
                  <p className="break-words">Delivery Address: {registry.delivery_address}</p>
                )}
                {registry.contact_number && (
                  <p>Contact: {registry.contact_number}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-3 w-full sm:w-auto justify-start sm:justify-end">
              {isOwner && (
                <>
                  <button
                    onClick={() => setShowEditRegistryModal(true)}
                    className="flex items-center space-x-1 px-3 py-2 bg-gray-800/50 rounded-md hover:text-white hover:bg-gray-700/50 text-sm"
                  >
                    <Edit className="w-4 h-4" />
                    <span>Edit Registry</span>
                  </button>
                  <button
                    onClick={() => setShowEditEventsModal(true)}
                    className="flex items-center space-x-1 px-3 py-2 bg-gray-800/50 rounded-md hover:text-white hover:bg-gray-700/50 text-sm"
                  >
                    <Calendar className="w-4 h-4" />
                    <span>Manage Events</span>
                  </button>
                  <button
                    onClick={handleExport}
                    className="flex items-center space-x-1 px-3 py-2 bg-gray-800/50 rounded-md hover:text-white hover:bg-gray-700/50 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export</span>
                  </button>
                </>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center space-x-1 px-3 py-2 bg-gray-800/50 rounded-md hover:text-white hover:bg-gray-700/50 text-sm"
              >
                <Share2 className="w-4 h-4" />
                <span>Share</span>
              </button>
            </div>
          </div>


          {/* Money Request Section - Only for Registry Owners */}
          {isOwner && (
            <MoneyRequestSection registryId={id!} currency={registry.currency} />
          )}

          <div id="gift-section" className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {registry.items.map((item) => {
              const progressPercentage = item.allows_group_gifting 
                ? (item.collected_amount / item.target_amount!) * 100
                : 0;

              return (
                <div
                  key={item.id}
                  className="bg-gray-800/50 backdrop-blur-sm rounded-lg overflow-hidden hover:bg-gray-700/50 transition-colors"
                >
                  <div className="p-4 sm:p-6 space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-medium break-words">{item.name}</h3>
                        {(isOwner || !item.allows_group_gifting) && (
                          <p className="text-sm">
                            {registry.currency} {item.price}
                          </p>
                        )}
                      </div>
                    </div>

                    {item.url && (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block hover:text-white text-sm underline"
                      >
                        View Product
                      </a>
                    )}

                    {item.is_claimed && !item.allows_group_gifting ? (
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2 text-sm">
                          <User className="w-4 h-4 flex-shrink-0" />
                          <span className="break-words">
                            {isOwner ? (
                              <>
                                Claimed by {claimInfo[item.id]?.name || 'Anonymous'}
                                {claimInfo[item.id]?.email && (
                                  <span className="block opacity-75 text-xs mt-1">
                                    {claimInfo[item.id].email}
                                  </span>
                                )}
                                {item.claimed_by && (
                                  <button
                                    onClick={() => handleThankYou(item)}
                                    className="block mt-2 text-primary hover:text-primary-600"
                                  >
                                    Send Thank You
                                  </button>
                                )}
                              </>
                            ) : (
                              'This gift has been claimed'
                            )}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col space-y-3">
                        {item.allows_group_gifting ? (
                          <>
                            <div className="w-full space-y-2">
                              <div className="flex justify-between text-sm">
                                <span>Progress: {progressPercentage.toFixed(1)}%</span>
                                {isOwner && (
                                  <span>
                                    {registry.currency} {item.collected_amount} of {registry.currency} {item.target_amount}
                                  </span>
                                )}
                              </div>
                              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-primary-600 to-primary-400 transition-all duration-300"
                                  style={{ width: `${progressPercentage}%` }}
                                />
                              </div>
                              {isOwner && contributions[item.id]?.length > 0 && (
                                <div className="mt-4 space-y-2">
                                  <h4 className="text-sm font-medium flex items-center">
                                    <DollarSign className="w-4 h-4 mr-1" />
                                    Contributors
                                  </h4>
                                  <div className="space-y-2">
                                    {contributions[item.id].map((contribution, index) => (
                                      <div key={index} className="text-sm flex justify-between">
                                        <span>{contribution.contributor_name}</span>
                                        <span>{registry.currency} {contribution.amount}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <button
                                onClick={() => handleGroupGift(item)}
                                disabled={item.is_claimed && progressPercentage >= 100}
                                className={`w-full flex items-center justify-center space-x-2 px-4 py-2 ${
                                  item.is_claimed && progressPercentage >= 100 
                                    ? 'bg-green-600 text-white' 
                                    : 'bg-primary text-white hover:bg-primary-600'
                                } rounded-md text-sm ${
                                  item.is_claimed && progressPercentage >= 100 ? 'cursor-default' : ''
                                }`}
                              >
                                <Gift className="w-4 h-4" />
                                <span>
                                  {item.is_claimed && progressPercentage >= 100 
                                    ? 'Fully Funded!' 
                                    : 'Contribute'}
                                </span>
                              </button>
                            </div>
                          </>
                        ) : (
                          <button
                            onClick={() => handleClaim(item)}
                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 text-sm"
                          >
                            <Gift className="w-4 h-4" />
                            <span>Claim Gift</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!user && (
            <div className="flex justify-center mt-8">
              <button
                onClick={handleSkipGift}
                className="px-6 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600 transition-colors"
                aria-label="Skip gift selection for now"
              >
                Skip Gift For Now
              </button>
            </div>
          )}

          {isOwner && (
            <div className="mt-8 bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 sm:p-6">
              <RSVPTable registryId={id!} />
            </div>
          )}
        </div>
        
        {isOwner && (
          <ThemeCustomizer
            registryId={id!}
            initialTheme={registry?.theme as ThemeConfig}
            onThemeChange={setTheme}
          />
        )}

        {showShareModal && (
          <ShareRegistryModal
            registryId={id!}
            onClose={() => setShowShareModal(false)}
          />
        )}

        {showClaimModal && selectedItem && (
          <GuestClaimModal
            item={selectedItem}
            onClose={() => setShowClaimModal(false)}
            onClaim={handleClaimSuccess}
            rsvpData={rsvpData || undefined}
          />
        )}

        {showPaymentModal && selectedItem && registry && (
          <PaymentModal
            item={selectedItem}
            currency={registry.currency}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={handleClaimSuccess}
            rsvpData={rsvpData || undefined}
            feeResponsibility={registry.fee_responsibility}
            platformFeePercentage={registry.platform_fee_percentage}
            gatewayFeePercentage={registry.gateway_fee_percentage}
          />
        )}

        {showGroupGiftModal && selectedItem && registry && (
          <GroupGiftModal
            item={selectedItem}
            registry={registry}
            onClose={() => setShowGroupGiftModal(false)}
            onContribute={handleContributionSuccess}
            rsvpData={rsvpData || undefined}
          />
        )}

        {showSuccessMessage && selectedItem && (
          <SuccessMessage
            type={successMessageType}
            item={selectedItem}
            onClose={handleSuccessClose}
          />
        )}

        {showThankYouModal && selectedItem && (
          <ThankYouModal
            item={selectedItem}
            registryId={registry.id}
            onClose={() => {
              setShowThankYouModal(false);
              setSelectedItem(null);
            }}
          />
        )}

        {showEditRegistryModal && (
          <EditRegistryModal
            registry={registry}
            onClose={handleEditRegistryClose}
            onUpdate={loadRegistry}
          />
        )}

        {showEditEventsModal && (
          <EditEventsModal
            registryId={registry.id}
            events={events}
            onClose={() => setShowEditEventsModal(false)}
            onUpdate={loadEvents}
          />
        )}

        {showEditItemModal && selectedItem && (
          <EditItemModal
            item={selectedItem}
            currency={registry.currency}
            onClose={() => {
              setShowEditItemModal(false);
              setSelectedItem(null);
            }}
            onUpdate={loadRegistry}
          />
        )}
      </ThemedLayout>
    </ThemeProvider>
  );
}